import { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { TIME_RANGE_OPTIONS } from '../../../utils/date';
import { DateRangeType, getDateRange } from '../../period-dropdown/DateRangePicker';
import { PRODUCT_TYPES } from '../partials/StaffFilters';
import { ExtraProps } from './useReportData';

interface CompanyFilters {
  showStaff: boolean;
  productCodes: string[];
  referralCode: string;
}

export interface StaffFilter {
  period: string | number;
  setPeriod: (period: string | number) => void;
  dateRange: DateRangeType;
  setDateRange: (range: DateRangeType) => void;
  filters: CompanyFilters;
  handleSetShowStaff: (param: boolean) => void;
  handleSetProductType: (code: string) => void;
  handleSearchReferralCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearFilters: () => void;
  extraFilterApiParams: ExtraProps;
}

const initialFilters = {
  showStaff: false,
  productCodes: [PRODUCT_TYPES[0].code, PRODUCT_TYPES[1].code, PRODUCT_TYPES[2].code],
  referralCode: '',
};

export const useStaffFilters = () => {
  const [filters, setFilters] = useState(initialFilters);

  const [period, setPeriod] = useState(TIME_RANGE_OPTIONS[2].code);
  const [dateRange, setDateRange] = useState<DateRangeType>(getDateRange(Number(TIME_RANGE_OPTIONS[2].code)));
  const handleSetShowStaff = (checked: boolean) => {
    setFilters((prev) => ({ ...prev, showStaff: !!checked }));
  };

  const [debouncedReferralCode] = useDebounce(filters.referralCode, 1000);

  const extraFilterApiParams = useMemo(
    () => ({
      showStaff: filters.showStaff,
      productCodes: filters.productCodes,
      referralCode: debouncedReferralCode,
    }),
    [filters.showStaff, filters.productCodes, debouncedReferralCode]
  );

  const handleSetProductType = (productCode: string) => {
    const productCodes = filters.productCodes.includes(productCode)
      ? filters.productCodes.filter((code) => code !== productCode)
      : [...filters.productCodes, productCode];

    return setFilters((prev) => ({ ...prev, productCodes }));
  };

  const handleSearchReferralCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, referralCode: e.target.value }));
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setPeriod(TIME_RANGE_OPTIONS[2].code);
  };

  return {
    period,
    setPeriod,
    dateRange,
    setDateRange,
    filters,
    debouncedReferralCode,
    handleSetShowStaff,
    handleSetProductType,
    handleSearchReferralCode,
    clearFilters,
    extraFilterApiParams,
  };
};
